/**
 * Trait chat panel — the appearance/personality capture surface, scoped to ONE target:
 *   • target "lagna"  → APPEARANCE: an AI chat about physical looks PLUS a photo upload.
 *   • target "moon"   → PERSONALITY: an AI chat about disposition/behaviour (no photo).
 *
 * Both write to the SAME structured trait answers the deterministic engine scores
 * (PLAN.md "Appearance capture"), but each chat sees and edits ONLY its own target's questions,
 * keeping its own conversation id + log so the two overlays don't collide. Saving merges the
 * chat's (scoped) result over the OTHER target's existing entries so neither erases the other.
 */

import { useEffect, useRef, useState } from "react";
import { useProfile } from "../state/ProfileContext";
import { trait_chat_turn } from "../lib/trait_chat";
import { extract_traits_from_photo } from "../lib/photo_traits";
import { carry_ai_cost, get_openai_api_key } from "../lib/openai_service";
import { TRAIT_QUESTIONS, age_years_from_birth, normalize_trait_answers, type TraitTarget } from "../lib/traits";
import { Banner, Spinner, useAsyncAction } from "../ui/common";

type ChatMsg = { role: "assistant" | "user"; text: string };

const QUESTION_BY_KEY: Record<string, (typeof TRAIT_QUESTIONS)[number]> = Object.fromEntries(
  TRAIT_QUESTIONS.map((q) => [q.key, q]),
);

export function option_label(key: string, opt: string): string {
  return QUESTION_BY_KEY[key]?.options[opt]?.label ?? opt;
}

/** Is this question key in the given target's lane? */
function inTarget(key: string, target: TraitTarget): boolean {
  return QUESTION_BY_KEY[key]?.target === target;
}


interface PhotoSuggestion {
  key: string;
  opt: string;
  /** "has" → applies to traits; "not" → applies to rejected_traits (an absolute gate). */
  polarity: "has" | "not";
  used: boolean;
}

/** The appearance (lagna) or personality (moon) discussion, rendered inside an overlay modal. */
export function TraitChatPanel({ target }: { target: TraitTarget }) {
  const { user_profile, run_data, mutate, reload } = useProfile();
  const chatAction = useAsyncAction();
  const photoAction = useAsyncAction();
  const [draft, setDraft] = useState("");
  // Optimistic in-flight turn: the just-sent user line + the assistant reply as it streams in.
  const [streaming, setStreaming] = useState<{ user: string; reply: string } | null>(null);
  const [suggestions, setSuggestions] = useState<PhotoSuggestion[] | null>(null);
  const [observations, setObservations] = useState<string[]>([]);
  const photoLikelihood = useRef<Record<string, number> | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const logEndRef = useRef<HTMLDivElement>(null);
  const hasKey = !!get_openai_api_key();
  const isAppearance = target === "lagna";

  // Keep the newest message in view as it streams (ChatGPT-style).
  useEffect(() => {
    logEndRef.current?.scrollIntoView({ block: "end" });
  }, [streaming, user_profile]);

  if (!user_profile) return null;
  const age = age_years_from_birth(user_profile.birth?.date);
  const idField = isAppearance ? "appearance_conversation_id" : "personality_conversation_id";
  const logField = isAppearance ? "appearance_chat" : "personality_chat";
  const chat: ChatMsg[] = (user_profile[logField] ?? []) as ChatMsg[];

  // Merge the chat's (target-scoped) result over the OTHER target's saved entries, so the
  // appearance chat never wipes personality traits and vice-versa.
  const mergeScoped = (existing: Record<string, any>, scoped: Record<string, any>) => {
    const out: Record<string, any> = {};
    for (const [k, v] of Object.entries(existing ?? {})) if (!inTarget(k, target)) out[k] = v;
    for (const [k, v] of Object.entries(scoped ?? {})) out[k] = v;
    return out;
  };

  async function runTurn(message: string) {
    const text = message.trim();
    setDraft("");
    setStreaming({ user: text, reply: "" });
    await chatAction.run("Thinking…", async () => {
      const run = structuredClone(run_data ?? {});
      const result = await trait_chat_turn(
        (user_profile![idField] as string | null) ?? null,
        text,
        user_profile!.traits ?? {},
        run,
        user_profile!.slug ?? null,
        age,
        user_profile!.rejected_traits ?? {},
        target,
        (partial) => setStreaming((s) => ({ user: s?.user ?? text, reply: partial })),
      );
      if (result.error) {
        // The turn may have been BILLED before failing on our side — keep its spend on the record.
        mutate((_p: any, r: any) => carry_ai_cost(r, run));
        throw new Error(String(result.error));
      }
      mutate((p: any, r: any) => {
        const log: ChatMsg[] = Array.isArray(p[logField]) ? [...p[logField]] : [];
        if (text) log.push({ role: "user", text });
        if (result.reply) log.push({ role: "assistant", text: result.reply });
        p[logField] = log;
        p[idField] = result.conversation_id ?? p[idField];
        if (result.traits) p.traits = mergeScoped(p.traits ?? {}, result.traits);
        if (result.rejected) p.rejected_traits = mergeScoped(p.rejected_traits ?? {}, result.rejected);
        // AI influence presets (astrological relevance) for this target's traits — merged so the
        // user's later manual edits on the Saved-information table aren't clobbered for OTHER keys.
        if (result.influence && Object.keys(result.influence).length > 0) {
          const ti: Record<string, Record<string, string>> = { ...(p.trait_influence ?? {}) };
          for (const [k, opts] of Object.entries(result.influence as Record<string, Record<string, string>>)) {
            ti[k] = { ...(ti[k] ?? {}), ...opts };
          }
          p.trait_influence = ti;
        }
        // Only the APPEARANCE chat sets rising_sign_likelihood (a rising-sign estimate); the
        // personality chat's estimate is about the Moon sign and must not overwrite it.
        if (isAppearance && result.likelihood && Object.keys(result.likelihood).length > 0) {
          p.rising_sign_likelihood = result.likelihood;
        }
        p.loop_count = 0;
        r.log = run.log ?? r.log;
        carry_ai_cost(r, run);
      });
      reload();
    });
    // The persisted log now carries this turn — drop the optimistic overlay.
    setStreaming(null);
  }

  async function analysePhoto(file: File) {
    await photoAction.run("Reading visible features from the photo…", async () => {
      const bytes = new Uint8Array(await file.arrayBuffer());
      const run = structuredClone(run_data ?? {});
      const result = await extract_traits_from_photo(
        bytes,
        file.type || "image/jpeg",
        run,
        user_profile!.slug ?? null,
        age,
      );
      // Vision calls are the priciest ones here — record the spend whether or not the read
      // produced usable traits.
      mutate((_p: any, r: any) => carry_ai_cost(r, run));
      if (result.error) throw new Error(String(result.error));
      const next: PhotoSuggestion[] = [];
      for (const [key, opts] of Object.entries(result.traits as Record<string, string[]>)) {
        for (const opt of opts) next.push({ key, opt, polarity: "has", used: false });
      }
      for (const [key, opts] of Object.entries((result.rejected ?? {}) as Record<string, string[]>)) {
        for (const opt of opts) next.push({ key, opt, polarity: "not", used: false });
      }
      photoLikelihood.current =
        result.likelihood && Object.keys(result.likelihood).length > 0 ? result.likelihood : null;
      setSuggestions(next);
      setObservations(result.observations ?? []);
      if (next.length === 0) {
        photoAction.setError("The photo didn't show any of the trait categories clearly — try the chat instead.");
      }
    });
  }

  function useSuggestion(s: PhotoSuggestion) {
    mutate((p: any) => {
      const field = s.polarity === "not" ? "rejected_traits" : "traits";
      const store = { ...(p[field] ?? {}) };
      const current: string[] = Array.isArray(store[s.key]) ? [...store[s.key]] : [];
      if (!current.includes(s.opt)) current.push(s.opt);
      store[s.key] = current;
      p[field] = store;
      if (photoLikelihood.current) p.rising_sign_likelihood = photoLikelihood.current;
      p.loop_count = 0;
    });
    setSuggestions((prev) =>
      prev ? prev.map((x) => (x.key === s.key && x.opt === s.opt && x.polarity === s.polarity ? { ...x, used: true } : x)) : prev,
    );
    reload();
  }

  return (
    <div>
      {!hasKey && (
        <Banner kind="warn">
          No OpenAI key is configured, so the chat{isAppearance ? " and photo analysis are" : " is"} unavailable.
        </Banner>
      )}

      {hasKey && isAppearance && (
        <>
          <h4>📷 From a photo</h4>
          <p className="caption">
            A photo answers the physical questions at a glance. Detected features are only{" "}
            <em>suggestions</em> — press <strong>Use</strong> on the ones that are right. It also flags
            traits the person <strong>clearly is NOT</strong> (red — these become rejected traits).
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            disabled={photoAction.pending}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void analysePhoto(f);
              if (fileRef.current) fileRef.current.value = "";
            }}
          />
          {photoAction.pending && <p className="caption"><Spinner /> {photoAction.message}</p>}
          {photoAction.error && <Banner kind="error">{photoAction.error}</Banner>}
          {observations.length > 0 && <p className="caption">Seen in the photo: {observations.join("; ")}</p>}
          {suggestions && suggestions.length > 0 && (
            <div className="photo-suggestions">
              {suggestions.map((s) => (
                <div className={`event-item${s.polarity === "not" ? " rejected" : ""}`} key={`${s.polarity}:${s.key}:${s.opt}`}>
                  <div>
                    <strong>
                      {s.polarity === "not" ? "Definitely not: " : ""}
                      {option_label(s.key, s.opt)}
                    </strong>
                    <div className="caption">{QUESTION_BY_KEY[s.key]?.prompt ?? s.key}</div>
                  </div>
                  <button disabled={s.used} onClick={() => useSuggestion(s)}>
                    {s.used ? "Used ✓" : s.polarity === "not" ? "Use (reject)" : "Use"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {hasKey && (
        <>
          <h4 style={{ marginTop: isAppearance ? "1rem" : 0 }}>
            💬 {isAppearance ? "Describe their appearance" : "Describe their personality"}
          </h4>
          <p className="caption">
            {isAppearance
              ? "The assistant asks up to 3 concrete questions about physical looks at a time — including what the person definitely is NOT."
              : "The assistant asks up to 3 questions about observable behaviour & disposition at a time — including what they definitely are NOT. Personality is read from the Moon sign."}{" "}
            It already knows everything saved below, so it won't repeat itself.
          </p>
          <div className="trait-chat-log">
            {chat.length === 0 && !streaming && <p className="muted">No conversation yet — start below.</p>}
            {chat.map((m, i) => (
              <div key={i} className={`chat-msg ${m.role}`}>
                <span>{m.text}</span>
              </div>
            ))}
            {chatAction.pending && streaming && (
              <>
                {streaming.user && (
                  <div className="chat-msg user">
                    <span>{streaming.user}</span>
                  </div>
                )}
                <div className="chat-msg assistant">
                  {streaming.reply ? (
                    <span>
                      {streaming.reply}
                      <span className="chat-caret" />
                    </span>
                  ) : (
                    <span className="chat-typing" aria-label="Assistant is typing">
                      <span />
                      <span />
                      <span />
                    </span>
                  )}
                </div>
              </>
            )}
            <div ref={logEndRef} />
          </div>
          {chatAction.error && <Banner kind="error">{chatAction.error}</Banner>}
          <div className="row" style={{ alignItems: "flex-end" }}>
            <textarea
              rows={2}
              style={{ flex: 1 }}
              value={draft}
              disabled={chatAction.pending}
              placeholder={
                chat.length === 0
                  ? isAppearance
                    ? "Press Start (or type) — e.g. tall, lean, sharp face, definitely not heavy-set…"
                    : "Press Start (or type) — e.g. quiet & analytical, definitely not impulsive…"
                  : "Type an answer — you can also say what they're definitely NOT…  (Enter to send · Ctrl+Enter for a new line)"
              }
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Enter") return;
                // Shift+Enter keeps the browser default (newline). Ctrl/Cmd+Enter inserts a newline
                // at the caret. Plain Enter submits the turn (mirroring the Send button's guard).
                if (e.shiftKey) return;
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  const el = e.currentTarget;
                  const start = el.selectionStart ?? draft.length;
                  const end = el.selectionEnd ?? draft.length;
                  const next = draft.slice(0, start) + "\n" + draft.slice(end);
                  setDraft(next);
                  requestAnimationFrame(() => {
                    el.selectionStart = el.selectionEnd = start + 1;
                  });
                  return;
                }
                e.preventDefault();
                if (chatAction.pending || (chat.length > 0 && !draft.trim())) return;
                runTurn(draft);
              }}
            />
            <button
              className="primary trait-chat-send"
              aria-label={chat.length === 0 && !draft.trim() ? "Start" : "Send"}
              title={chat.length === 0 && !draft.trim() ? "Start" : "Send"}
              disabled={chatAction.pending || (chat.length > 0 && !draft.trim())}
              onClick={() => runTurn(draft)}
            >
              {chatAction.pending ? <Spinner /> : <span aria-hidden>▶</span>}
            </button>
          </div>
        </>
      )}

      {(() => {
        const labelsFor = (src: "traits" | "rejected_traits") =>
          Object.entries(normalize_trait_answers(user_profile![src] ?? {}))
            .filter(([k]) => inTarget(k, target))
            // delivery_type is a BIRTH FACT captured through the appearance chat, not a body
            // feature — prefix it so it doesn't read as an appearance trait.
            .flatMap(([k, opts]) => opts.map((o) => (k === "delivery_type" ? `born ${option_label(k, o)}` : option_label(k, o))));
        const has = labelsFor("traits");
        const not = labelsFor("rejected_traits");
        return (
          <div className="tc-summary">
            <strong>Saved {isAppearance ? "appearance" : "personality"} traits:</strong>{" "}
            {has.length ? has.join(", ") : <span className="muted">nothing yet — the chat above will fill this in</span>}
            {not.length > 0 && (
              <>
                {" · "}
                <span style={{ color: "var(--danger-ink)" }}>not: {not.join(", ")}</span>
              </>
            )}{" "}
            <span className="muted">· edit influence &amp; delete on the main page's Saved-information table.</span>
          </div>
        );
      })()}
    </div>
  );
}
